import { useMemo, type CSSProperties, type FC, type ReactNode } from 'react';
import { Dialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useAllowPinchZoom } from '@/hooks/useAllowPinchZoom';
import { usePageTheme } from '@/hooks/usePageTheme';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  noPaddingBottom?: boolean;
  /**
   * DialogPanel に追加で適用する inline style。
   * 既定で PageThemeContext の CSS 変数を受け取るため、通常は指定不要。
   * ここで同じキーを指定した場合はこちらが優先される。
   */
  panelStyle?: CSSProperties;
}

export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'lg',
  showCloseButton = true,
  closeOnBackdropClick = true,
  noPaddingBottom = false,
  panelStyle,
}) => {
  const pageTheme = usePageTheme();
  const mergedStyle = useMemo(
    () => ({ ...pageTheme, ...panelStyle }),
    [pageTheme, panelStyle],
  );
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
  };

  // モーダル表示時に背景のスクロールをロック（参照カウント方式で複数モーダル対応）
  useScrollLock(isOpen);
  // 拡大された状態でモーダルを開いても縮小できるようにする
  useAllowPinchZoom(isOpen);

  const handleClose = closeOnBackdropClick ? onClose : () => {};

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      {/* 背景オーバーレイ */}
      <DialogBackdrop className="fixed inset-0 bg-black/20 backdrop-blur-sm" />

      {/* モーダルコンテナ */}
      <div className="fixed inset-0 flex items-start justify-center p-4 pt-8 md:pt-16">
        <DialogPanel
          style={mergedStyle}
          className={clsx(
            'bg-white rounded-xl shadow-lg w-full max-h-[calc(100dvh-4rem)] md:max-h-[calc(100dvh-8rem)] flex flex-col border border-text-200',
            maxWidthClasses[maxWidth]
          )}
        >
          {/* ヘッダー（タイトル + 閉じるボタン） */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-text-200 flex-shrink-0">
              {title && (
                <DialogTitle className="text-lg font-semibold text-text-800">
                  {title}
                </DialogTitle>
              )}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="text-text-400 hover:text-text-600 transition-colors"
                  aria-label="閉じる"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              )}
            </div>
          )}

          {/* コンテンツエリア（スクロール可能） */}
          <div className={clsx('overflow-y-auto flex-1 p-6 pt-4', noPaddingBottom && 'pb-0')}>
            {children}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
